import { useEffect, useRef, useState } from 'react';
import { getAllSettings, setSetting, onSettingsChange } from '../lib/settings.js';

function Toggle({ checked, onChange, label, description, disabled, disabledTooltip }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      title={disabled ? disabledTooltip : undefined}
      disabled={disabled}
      className={`w-full flex items-start justify-between gap-3 px-3 py-2.5 rounded-md text-left transition-colors ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-panel2'
      }`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{label}</div>
        {description && (
          <div className="text-[11px] text-muted mt-0.5 leading-snug">{description}</div>
        )}
      </div>
      <div
        className={`mt-0.5 flex-shrink-0 w-9 h-5 rounded-full p-0.5 transition-colors ${
          disabled ? 'bg-border/50' : checked ? 'bg-accent' : 'bg-border'
        }`}
      >
        <div
          className={`w-4 h-4 rounded-full bg-white transition-transform ${checked && !disabled ? 'translate-x-4' : ''}`}
        />
      </div>
    </button>
  );
}

export default function SettingsMenu({ employee }) {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(getAllSettings());
  const ref = useRef(null);

  useEffect(() => {
    const off = onSettingsChange(setSettings);
    return off;
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = e => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(o => !o)}
        title="Settings"
        className="p-1.5 rounded-md hover:bg-panel border border-transparent hover:border-border transition-colors text-muted hover:text-text"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-64 rounded-lg border border-border bg-panel shadow-2xl z-40 p-1.5">
          {/* Theme toggle moved out of Settings into the top bar — see
              AppShell.jsx. Reach for the sun/moon icon next to the
              gear if you want to switch light/dark. */}
          <div className="px-3 py-1 text-[10px] uppercase tracking-wider text-muted">Feedback</div>
          <Toggle
            label="Sound effects"
            description="Subtle chime on Start / Stop / Complete"
            checked={settings.sound}
            onChange={v => setSetting('sound', v)}
          />
          <Toggle
            label="Confetti on Complete"
            description="Celebrate when you mark a task complete"
            checked={settings.confetti}
            onChange={v => setSetting('confetti', v)}
          />
          <Toggle
            label="Special Features"
            description="Welcome card on first login of the day, plus a friendly toast after each completed task."
            checked={settings.welcomeMode && Boolean(employee?.welcomeEnabled)}
            onChange={v => setSetting('welcomeMode', v)}
            disabled={!employee?.welcomeEnabled}
            disabledTooltip="I don't think you need this right now."
          />
        </div>
      )}
    </div>
  );
}
